import React, { useState, useEffect } from 'react';
import { AlertTriangle, Trash2, X, Check } from 'lucide-react';

const DeleteConfirmation = ({ type, deleteMode, onConfirm, onCancel }) => {
  const [countdown, setCountdown] = useState(5);
  const [isButtonEnabled, setIsButtonEnabled] = useState(false);
  const [isPulsing, setIsPulsing] = useState(true);
  const [userAgreed, setUserAgreed] = useState(false);
  const [confirmationText, setConfirmationText] = useState('');
  
  // Countdown timer for enabling the delete button
  useEffect(() => {
    const timer = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          clearInterval(timer);
          setIsButtonEnabled(true);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    
    return () => clearInterval(timer);
  }, []);

  // Pulsing effect
  useEffect(() => {
    const pulseInterval = setInterval(() => {
      setIsPulsing(prev => !prev);
    }, 800);
    return () => clearInterval(pulseInterval);
  }, []);

  const canDelete = isButtonEnabled && userAgreed && confirmationText === 'DELETE';

  // Update onConfirm call, invoked only if conditions are met.
  const handleConfirm = () => {
    if (canDelete) {
      onConfirm(); // Call the onConfirm callback
      onCancel()
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto">
      <div className="fixed z-40 w-full max-w-2xl">
        {/* Danger glow effect */}
        <div 
          className={`absolute inset-0 bg-gradient-to-br from-red-900/30 to-transparent rounded-xl filter blur-xl opacity-50 ${isPulsing ? 'scale-105' : 'scale-100'}`}
          style={{ transition: 'all 0.8s ease' }}
        />
          
        <div className="bg-gray-900/95 border border-red-900/50 rounded-xl shadow-2xl overflow-hidden relative backdrop-blur-sm">
          {/* Header */}
          <div className="bg-gradient-to-r from-red-900/80 to-red-900/50 px-6 py-4 flex items-center justify-between">
            <div className="flex items-center">
              <div className={`p-1.5 rounded-full bg-red-600/30 mr-3 ${isPulsing ? 'animate-pulse' : ''}`}>
                <AlertTriangle className="w-5 h-5 text-red-300" />
              </div>
              <h2 className="text-xl font-bold text-white tracking-tight">SECURE {type} DESTRUCTION</h2>
            </div>
            <button 
              onClick={onCancel}
              className="text-red-300 hover:text-white transition-colors p-1 rounded-full"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          
          {/* Pulsing border */}
          <div className={`h-0.5 w-full ${isPulsing ? 'bg-red-500/80' : 'bg-red-500/50'}`} />
          
          {/* Content */}
          <div className="p-6 space-y-6">
            <div className="text-center">
              <p className="text-white font-medium text-lg mb-2">
                You are about to <span className="text-red-400 font-bold">permanently destroy</span> {type}
              </p>
              <p className="text-gray-400 text-sm">
                This action uses {deleteMode} erasure protocols
              </p>
            </div>
            
            {/* Critical warning */}
            <div className={`bg-red-900/20 border ${isPulsing ? 'border-red-700/70' : 'border-red-700/40'} rounded-lg p-4 transition-all duration-800`}>
              <div className="flex">
                <AlertTriangle className="flex-shrink-0 w-5 h-5 text-red-400 mt-0.5 mr-2" />
                <div>
                  <p className="text-red-300 font-semibold text-sm uppercase tracking-wider mb-1">Irreversible Action</p>
                  <p className="text-gray-300 text-sm leading-snug">
                    This process will overwrite the {type} beyond any possible recovery, including forensic tools. 
                    <span className="font-bold text-red-300"> There is no undo.</span>
                  </p>
                </div>
              </div>
            </div>
            
            {/* Agreement checkbox */}
            <div className="flex items-start">
              <div className="flex items-center h-5">
                <input
                  id="agree"
                  type="checkbox"
                  checked={userAgreed}
                  onChange={(e) => setUserAgreed(e.target.checked)}
                  className="h-4 w-4 rounded border-gray-600 bg-gray-700 text-red-500 focus:ring-red-500 focus:ring-offset-gray-900"
                />
              </div>
              <label htmlFor="agree" className="ml-3 text-sm text-gray-300">
                I understand this action will permanently destroy data beyond any recovery method.
              </label>
            </div>
            
            {/* Confirmation input */}
            <div className="space-y-2">
              <label htmlFor="confirm-text" className="block text-sm font-medium text-gray-400">
                Type <span className="font-mono text-white">DELETE</span> to confirm:
              </label>
              <input
                id="confirm-text"
                type="text"
                value={confirmationText}
                onChange={(e) => setConfirmationText(e.target.value.toUpperCase())}
                className="w-full bg-gray-800 border border-gray-700 focus:border-red-500 rounded-md px-3 py-2 text-white text-center font-mono focus:outline-none focus:ring-1 focus:ring-red-500/50"
                placeholder="Type DELETE"
                autoComplete="off"
                spellCheck="false"
              />
            </div>
            
            {/* System status */}
            <div className="flex items-center justify-center text-sm">
              {!isButtonEnabled ? (
                <div className="flex items-center text-yellow-400">
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-yellow-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Initializing secure protocols... {countdown}s
                </div>
              ) : (
                <div className="flex items-center text-green-400">
                  <Check className="w-4 h-4 mr-1" />
                  Secure deletion ready
                </div>
              )}
            </div>
          </div>
          
          {/* Footer */}
          <div className="px-6 py-4 bg-gray-800/60 border-t border-gray-700/50 flex justify-between">
            <button
              onClick={onCancel}
              className="px-4 py-2 text-gray-300 hover:text-white transition-colors duration-200 rounded-md"
            >
              Cancel
            </button>
            
            <button
              onClick={handleConfirm}
              disabled={!canDelete}
              className={`px-4 py-2 rounded-md font-medium flex items-center transition-all duration-200 ${
                canDelete
                  ? 'bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white shadow-lg shadow-red-900/20'
                  : 'bg-gray-700 text-gray-500 cursor-not-allowed'
              }`}
            >
              <Trash2 className={`w-4 h-4 mr-2 ${canDelete ? 'text-white' : 'text-gray-500'}`} />
              {canDelete ? (
                <span className="font-bold">Destroy {type}</span>
              ) : (
                'Confirm Requirements'
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DeleteConfirmation;